import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GetPastSurveyCacheKey } from '../queries/useGetPastSurveys.ts';
import { GetScheduledSurveysCacheKey } from '../queries/useGetPlannedSurveys.ts';
import { toast } from 'react-toastify';

export type SurveyForm = {
  title: string;
  description: string;
  teamId: number;
  templateId: number;
  scheduledTime?: Date;
  frequency?: string;
  deadlineInDays?: number;
  daysBeforeDeadlineToNotify?: number;
};

type CreateScheduledSurveyResponse = {
  scheduledSurvey: unknown;
  wasAdjusted: boolean;
  requestedTime?: string;
  adjustedTime?: string;
  adjustmentReason?: 'weekend' | 'holiday' | 'outside_work_hours';
};

type CreateSurveyResponse = SurveyForm | CreateScheduledSurveyResponse;

export const createSurvey = (surveyForm: SurveyForm): Promise<{ data: CreateSurveyResponse }> => {
  return axios.post('/api/surveys/create', surveyForm);
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('nb-NO', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getAdjustmentReasonText = (reason: string): string => {
  const reasons = {
    weekend: 'i helgen',
    holiday: 'på en helligdag',
    outside_work_hours: 'utenfor arbeidstid',
  };
  return reasons[reason as keyof typeof reasons] || 'utenfor planlagt tid';
};

type UseCreateSurveyMutationOptions = {
  onSuccess?: (response: { data: CreateSurveyResponse }) => void;
  onError?: (error: Error) => void;
};

export const useCreateSurveyMutation = (
  teamId: number,
  options?: UseCreateSurveyMutationOptions,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (surveyForm: SurveyForm) => createSurvey(surveyForm),
    onSuccess: (response) => {
      const data = response.data;

      // Show adjustment feedback if time was adjusted
      if (
        typeof data === 'object' &&
        data !== null &&
        'wasAdjusted' in data &&
        data.wasAdjusted
      ) {
        const scheduledResponse = data as CreateScheduledSurveyResponse;

        toast.info(
          `Tidspunktet ble justert fra ${formatDate(scheduledResponse.requestedTime!)} til ${formatDate(scheduledResponse.adjustedTime!)} fordi det opprinnelige tidspunktet var ${getAdjustmentReasonText(scheduledResponse.adjustmentReason!)}.`,
          {
            autoClose: 8000,
          }
        );
      }

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: [GetPastSurveyCacheKey.PastSurvey, teamId],
      });
      queryClient.invalidateQueries({
        queryKey: [GetScheduledSurveysCacheKey.ScheduledSurveys, teamId],
      });

      // Call custom onSuccess callback if provided
      options?.onSuccess?.(response);
    },
    onError: options?.onError,
  });
};
